/**
 * Frozen pre-fee service-agreement clauses (agreement version
 * 2026-06-04, the last version before the 3.5% card-fee disclosure).
 *
 * Rendered by AgreementView only, and only for legacy signed records
 * that carry no signedAgreementSnapshot (signings before 2026-05-03,
 * when the snapshot shipped). Falling back to the LIVE clauses there
 * would retroactively rewrite the customer's permanent signed record
 * every time the live copy changes; every pre-snapshot signing
 * predates the fee disclosure, so this frozen set is the correct text
 * for all of them.
 *
 * Never edit this file to match new live clauses. It is a historical
 * record, not a template.
 */

export const LEGACY_PRE_FEE_SERVICE_AGREEMENT = {
  intro: "Here's what you're agreeing to. Plain English, no fine print surprises.",
  cards: [
    {
      title: 'Your first visit',
      body: "It is important that your pool be clean, algae free, and chemically balanced when we begin your weekly service. Some pools may require additional work before this can begin. On your first visit we will do a full inspection and communicate any additional work that needs to be done before we can begin.",
    },
    {
      title: 'Billed monthly on the 1st',
      body: "Our standard rate covers four weekly visits per month. Occasionally the calendar gives us a month with five weeks; when that happens we simply add the cost of that one extra visit (25%) to keep your pool sparkling. Conversely, if we visit fewer than four times in a month (bad weather, something on us, etc.), we automatically discount your bill 25% per missed visit. No hidden fees, just fair service.",
    },
    {
      title: 'Prorated first month',
      body: "Your first invoice is prorated based on the number of times we provide service that month.",
    },
    {
      title: 'Photo report after every visit',
      body: "Your technician sends a timestamped photo report by email after each service so you can see the finished pool, even if you're not home.",
    },
    {
      title: 'Pool access is your responsibility',
      body: "We need access on your service day to keep your pool on schedule. If we can't get in, the visit is skipped and the missed week is not credited.",
      details: "If your property requires a key, please leave us with two copies. If we arrive and can't access the pool (locked gate, changed locks, dog out, anything else), your tech will skip the visit and return on your next regular service day. If you'd rather we come back the same week, there's a $45 trip charge. Our techs won't jump or climb walls or fences. We'll always do our best to service on the same day each week, and if your regular tech can't make it, another tech will cover. Help us help you by making sure we can reach the pool.",
    },
    {
      title: 'Filtration is your responsibility',
      body: 'Your pump needs to run long enough to keep the water moving. Aim for at least 8 hours a day in warm months and 5 hours a day in cool months.',
      details: "Insufficient filtration causes algae and cloudy water and means we have to use more chemicals to fix it; those issues are not covered by our service guarantee. Topping off pool water is also on you: when the level drops below the skimmer, the pump can't pull water through the filter. Our techs don't add water to any pool, spa, or water feature, but if your pool needs a fill we'll leave a door hanger so you know. (If you'd rather skip the manual fills, ask us about installing an automatic water leveler.)",
    },
    {
      title: 'Weather conditions',
      body: "On rainy days we focus on chemistry and baskets. After windy weather, full debris cleanup may take more than one visit.",
      details: "On rainy days your tech will check chemistry, add chemicals, and empty the skimmer and pump baskets. Full cleanings while it's pouring don't make sense. After a windy stretch, debris keeps blowing in for a few days; sometimes it takes more than one visit to fully clear out a pool because techs are dealing with the same conditions across their whole route that day.",
      // Skipped on chem-only and chem-and-brush plans because the
      // clause is about full-service debris cleanup, which isn't part
      // of those tiers' scope.
      excludeForTiers: ['chem_only', 'chem_brush'],
    },
    {
      title: 'Pool covers',
      body: "Please remove safety nets and solar covers before your service day. We don't put them back on after the visit. Automatic covers should be open on your service day.",
      details: "Safety nets and solar pool covers need to be removed prior to service. We don't put safety nets or solar covers back on after servicing the pool. Automatic pool covers must be open on your service day so we can do a full visit.",
    },
    {
      title: 'Equipment repairs are not included',
      body: "Mechanical problems with pumps, filters, heaters, salt cells, automation, plaster, or tile aren't included in your monthly rate. If something goes wrong, we'll let you know what the repair would be. Cabana can handle all repairs and upgrades you need for pools and spas.",
    },
    {
      title: 'Specialty treatments billed separately',
      body: 'Standard chemicals (chlorine, acid, balancing) are included. The treatments below are recommended as needed and billed at the time of service.',
      addons: [
        { name: 'DE / Cartridge Filter Cleaning', frequency: 'Every 3 months or as needed' },
        { name: 'Algaecides', frequency: 'Once a year' },
        { name: 'Conditioner (Stabilizer)', frequency: '1-2 times a year' },
        { name: 'Diatomaceous Earth', frequency: 'As needed' },
        { name: 'Salt Cell Cleaning', frequency: 'Every 3-6 months', requires: 'salt' },
        { name: 'Spa Drain, Clean & Fill', frequency: 'Every 3-4 months', requires: 'spa-only' },
      ],
    },
    {
      title: 'Cancel anytime, 30 days notice',
      body: "We're excited to take care of your pool with this month-to-month service. It keeps going until you or we decide it's time to stop. If you ever need to cancel, just let us know in writing 30 days in advance. When service ends, any final payments are due.",
    },
    {
      title: 'Auto-pay authorization',
      body: "You authorize Cabana to charge the credit card or bank account you provided above on the 1st of each month for that month's service, plus any approved one-time charges or specialty treatments. We email a receipt for every charge. You can update or remove the payment method anytime by replying to any invoice.",
    },
  ],
  consentText: 'I authorize Cabana to charge the payment method above on the 1st of each month for monthly pool service, plus approved one-time and specialty charges as described. I have read the agreement above and agree to the terms. I can cancel anytime with 30 days notice.',
  esignDisclosure: "By clicking Start My Service you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",
  oneTimeEsignDisclosure: "By clicking Confirm Services you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",
  oneTimeCards: [
    {
      title: 'Yard access is your responsibility',
      body: "We need access to the work area on your scheduled service day. If our tech can't get in, a $45 trip charge applies for the return visit.",
    },
    {
      title: 'Warranty',
      body: "All parts and materials carry the manufacturer's standard warranty. Labor is warranted for 30 days from completion.",
    },
    {
      title: 'Liability',
      body: "Cabana carries multimillion-dollar liability insurance. You're responsible for any pre-existing conditions or damage not caused by our work.",
    },
    {
      title: 'Additional work',
      body: "Any work beyond the original scope requires your written approval and may incur additional charges. We won't do extra work without your sign-off.",
    },
    {
      title: 'Payment',
      body: "Past due accounts are subject to a 1.5% monthly service charge. If your account goes to collections, you're responsible for collection costs.",
    },
    {
      title: 'Charge authorization',
      body: 'You authorize Cabana to charge the credit card or bank account you provided above for the one-time services listed in this proposal at the time of service. We email a receipt for every charge.',
    },
  ],
  oneTimeConsentText: 'I authorize Cabana to charge the payment method above for the one-time services listed in this proposal at the time of service. I have read the agreement above and agree to the terms.',
  // Net 30 variant: same one-time scope clauses (yard access, warranty,
  // liability, additional work) but the payment block is replaced --
  // no card on file, invoice issued at completion, payment due 30 days
  // from invoice date. Selected per-deal by the `net-30` tag on the
  // Sell deal (see api/quote/[dealId].js backfill).
  oneTimeNet30Cards: [
    {
      title: 'Yard access is your responsibility',
      body: "We need access to the work area on your scheduled service day. If our tech can't get in, a $45 trip charge applies for the return visit.",
    },
    {
      title: 'Warranty',
      body: "All parts and materials carry the manufacturer's standard warranty. Labor is warranted for 30 days from completion.",
    },
    {
      title: 'Liability',
      body: "Cabana carries multimillion-dollar liability insurance. You're responsible for any pre-existing conditions or damage not caused by our work.",
    },
    {
      title: 'Additional work',
      body: "Any work beyond the original scope requires your written approval and may incur additional charges. We won't do extra work without your sign-off.",
    },
    {
      title: 'Net 30 invoicing',
      body: "We invoice for the services in this proposal upon completion. Payment is due within 30 days of the invoice date.",
    },
    {
      title: 'Past due accounts',
      body: "Invoices unpaid after 30 days are subject to a 1.5% monthly service charge. If your account goes to collections, you're responsible for collection costs.",
    },
  ],
  oneTimeNet30ConsentText: 'I agree to the services and pricing in this proposal. Cabana will invoice me at completion and I will remit payment within 30 days of the invoice date.',
  oneTimeNet30EsignDisclosure: "By clicking Confirm Services you're signing this agreement electronically under the U.S. ESIGN Act. We'll email you a copy. You can request a paper copy or withdraw consent by replying to any invoice.",
};
